// "signup-modal-flow" experiment: what a signed-out visitor gets after
// submitting a video (pasted link or picked file) on a marketing page.
// - control: the old flow, a redirect to /signup, whose confirmation email
//   carries a link.
// - test: signing up in a dialog over the page (components/SignupDialog.tsx),
//   confirming with a code from the email typed into it.
// Evaluated (and the exposure recorded) once, at that submit, in VideoInput,
// so both arms are the same population. Delete with the experiment.

export type SignupFlowVariant = "control" | "test";

export const SIGNUP_FLOW_FLAG_KEY = "signup-modal-flow";

// Stored on the new Supabase user as `user_metadata.signup_flow` by the test
// arm's signups. The "Confirm signup" email template branches on it:
//   {{ if eq .Data.signup_flow "code" }} …{{ .Token }}… {{ else }} …link… {{ end }}
// so control (and every signup outside the experiment) keeps today's email.
export const SIGNUP_FLOW_EMAIL_CODE = "code";

// Anything but the test arm (control, flag missing, flags not loaded) keeps
// today's flow.
export const signupFlowVariantFor = (
  variant: string | boolean | undefined | null,
): SignupFlowVariant => (variant === "test" ? "test" : "control");

const HAD_SESSION_KEY = "klap_had_session";

// Set whenever this browser has a signed-in session (usePostHogIdentify).
// Survives sign-out, unlike PostHog's identity (posthog.reset()).
export const markHadSession = () => {
  try {
    localStorage.setItem(HAD_SESSION_KEY, "1");
  } catch {
    // No storage: they may be bucketed again later, which is all it costs.
  }
};

// Someone who has had an account here stays out of the experiment (today's
// flow, no exposure): it's about new visitors, and after a sign-out PostHog
// would re-bucket them under a fresh anonymous id, possibly into the other
// arm.
export const isReturningVisitor = (
  posthogUserState: unknown,
): boolean => {
  if (posthogUserState === "identified") return true;
  try {
    return localStorage.getItem(HAD_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};
